import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";

interface PublicModeConfigDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectId: string;
  currentMode: 'admin_only' | 'open';
  initialEmailCheckBlacklist?: boolean;
  initialEmailCheckWhitelist?: boolean;
  initialEmailCheckAi?: boolean;
  initialEmailCheckAiPrompt?: string | null;
}

export function PublicModeConfigDialog({
  open,
  onOpenChange,
  projectId,
  currentMode,
  initialEmailCheckBlacklist = false,
  initialEmailCheckWhitelist = false,
  initialEmailCheckAi = false,
  initialEmailCheckAiPrompt = "",
}: PublicModeConfigDialogProps) {
  const { toast } = useToast();
  const qc = useQueryClient();
  const isAdminOnly = currentMode === 'admin_only';

  const [blacklist, setBlacklist] = useState(initialEmailCheckBlacklist);
  const [whitelist, setWhitelist] = useState(initialEmailCheckWhitelist);
  const [ai, setAi] = useState(initialEmailCheckAi);
  const [aiPrompt, setAiPrompt] = useState(initialEmailCheckAiPrompt || "");

  useEffect(() => {
    if (open) {
      setBlacklist(initialEmailCheckBlacklist);
      setWhitelist(initialEmailCheckWhitelist);
      setAi(initialEmailCheckAi);
      setAiPrompt(initialEmailCheckAiPrompt || "");
    }
  }, [open, initialEmailCheckBlacklist, initialEmailCheckWhitelist, initialEmailCheckAi, initialEmailCheckAiPrompt]);

  const mutation = useMutation({
    mutationFn: async (newMode: 'admin_only' | 'open') => {
      const res = await apiRequest('PATCH', `/api/projects/${projectId}/communication-mode`, {
        mode: newMode,
        emailCheckBlacklist: blacklist,
        emailCheckWhitelist: whitelist,
        emailCheckAi: ai,
        emailCheckAiPrompt: aiPrompt || null,
      });
      if (!res.ok) throw new Error('Failed to update communication settings');
      return res.json();
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['/api/tree/snapshot'] });
      qc.invalidateQueries({ queryKey: ['/api/projects', projectId] });
      toast({
        title: data.communicationMode === 'open' ? 'Communications opened' : 'Communications restricted',
        description: data.communicationMode === 'open'
          ? 'All assigned team members can now see new email pulses on this project.'
          : 'New email pulses on this project will be visible to admins only.',
      });
      onOpenChange(false);
    },
    onError: (err: any) => {
      toast({ title: 'Update failed', description: err?.message || 'Could not save communication settings', variant: 'destructive' });
    },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="navy-card border-gray-700 max-w-md">
        <DialogHeader>
          <DialogTitle>{isAdminOnly ? 'Open Communications' : 'Communications Settings'}</DialogTitle>
          <DialogDescription className="text-gray-400">
            {isAdminOnly
              ? 'Configure per-project email checks before opening this project to all assigned team members.'
              : 'Configure per-project email checks. Switching back to admin-only hides new email pulses from non-admins.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-gray-200">Blacklist check</Label>
              <p className="text-xs text-gray-500">Skip incoming emails matching the global blacklist keywords.</p>
            </div>
            <Switch checked={blacklist} onCheckedChange={setBlacklist} data-testid="switch-email-check-blacklist" />
          </div>

          <div className="flex items-center justify-between">
            <div>
              <Label className="text-gray-200">Whitelist bypass</Label>
              <p className="text-xs text-gray-500">Auto-approve emails from globally whitelisted senders/projects.</p>
            </div>
            <Switch checked={whitelist} onCheckedChange={setWhitelist} data-testid="switch-email-check-whitelist" />
          </div>

          <div className="flex items-center justify-between">
            <div>
              <Label className="text-gray-200">AI content review</Label>
              <p className="text-xs text-gray-500">Run ChatGPT review for incoming emails on this project.</p>
            </div>
            <Switch checked={ai} onCheckedChange={setAi} data-testid="switch-email-check-ai" />
          </div>

          <div>
            <Label className="text-gray-200">Project-specific AI prompt</Label>
            <p className="text-xs text-gray-500 mb-1">Appended to the global ChatGPT prompt as extra project context.</p>
            <Textarea
              value={aiPrompt}
              onChange={(e) => setAiPrompt(e.target.value)}
              placeholder="e.g. This project handles confidential VFX deliveries. Reject anything unrelated."
              rows={4}
              className="bg-gray-900 border-gray-700"
              data-testid="textarea-email-check-ai-prompt"
              disabled={!ai}
            />
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={mutation.isPending}>Cancel</Button>
          {isAdminOnly ? (
            <Button
              onClick={() => mutation.mutate('open')}
              disabled={mutation.isPending}
              data-testid="button-open-communications"
            >
              {mutation.isPending ? 'Saving…' : 'Open communications'}
            </Button>
          ) : (
            <>
              <Button
                variant="secondary"
                onClick={() => mutation.mutate('open')}
                disabled={mutation.isPending}
                data-testid="button-save-email-checks"
              >
                Save settings
              </Button>
              <Button
                variant="destructive"
                onClick={() => mutation.mutate('admin_only')}
                disabled={mutation.isPending}
                data-testid="button-restrict-communications"
              >
                Restrict to admins
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
